import { useCallback, useEffect, useMemo, useState } from 'react';
import { trackException } from 'helpers/errorTracker';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch, useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';

import { roles } from 'helpers/roles';
import { useClaimOrganizationMutation, useUpdateNetworkKindMutation } from 'services/organizationsV2';
import { useAddNetworkAdminMutation } from 'services/networkAdmins';
import strings from 'strings';
import { setActiveNetwork, updateNetwork, setNetworks } from '../../../actions/networksActions';
import { trackEvent } from '../../../helpers/analytics';
import { setUser } from '../../../actions/userActions';

import { networksSelector, userSelector } from '../../../redux/selectors';
import { useLazyGetUserQuery, useUpdateUserMutation } from '../../../services/user';
import { useUpdateUserGoalsMutation } from '../../../services/userV2';
import { checkExtensionAvailability } from '../../../services/chromeExtension';
import { legalDocumentsVersion } from '../../../settings/legalDocumentsVersion';
import { useCompanyNetwork } from './useCompanyNetwork';

export const useOnboarding = () => {
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
  const { path, params } = useRouteMatch();
  const { networks } = useSelector(networksSelector);
  const user = useSelector(userSelector);
  const localNetwork = useMemo(
    () => Object.values(networks).find(({ slug }) => slug === params?.networkSlug),
    [networks, params?.networkSlug],
  );
  const {
    network: companyNetwork,
    isLoading: isLoadingCompanyNetwork,
    isFetching: isFetchingCompanyNetwork,
    company,
  } = useCompanyNetwork();

  const network = localNetwork || companyNetwork;
  const [updateUser, { isLoading: isLoadingUpdateUser }] = useUpdateUserMutation();
  const [add, { isLoading: isLoadingAddAdmin }] = useAddNetworkAdminMutation();

  const dispatch = useDispatch();
  const history = useHistory();
  const [users, setUsers] = useState([
    { id: 1, name: '', email: '', role: roles[1] },
    { id: 2, name: '', email: '', role: roles[1] },
    { id: 3, name: '', email: '', role: roles[1] },
  ]);
  const [networkKind, setNetworkKind] = useState(network?.kind);
  const [goals, setGoals] = useState(new Set(network?.userGoals ?? []));
  const [updateUserGoals, { isLoading: isLoadingUpdateUserGoals }] = useUpdateUserGoalsMutation();
  const [updateKind, { isLoading: isLoadingUpdateNetworkKind }] = useUpdateNetworkKindMutation();
  const [claimOrganization, { isLoading: isClaimingOrganization }] = useClaimOrganizationMutation();
  const [getUser, { isLoading: isLoadingGetUser }] = useLazyGetUserQuery();

  useEffect(() => {
    checkExtensionAvailability()
      .then((availability) => {
        setIsExtensionInstalled(availability);
      })
      .catch(() => {
        setIsExtensionInstalled(false);
      });
  }, []);

  const addAdmin = useCallback(
    async (usersToAdd) => Promise.allSettled(usersToAdd.map((admin) => add({ networkId: network?.id, admin }))),
    [add, network],
  );

  const getNextStep = useCallback(
    async (step, { slug: defaultSlug, ...props } = {}) => {
      const slug = defaultSlug || network?.slug || companyNetwork?.slug;
      switch (step) {
        case 'profile':
          trackEvent('admin_onboarding:finish_step', { step_name: 'account' });
          return `/networks/${slug}/welcome/onboarding/network-kind`;
        case 'network-kind':
          setNetworkKind(props);
          trackEvent('admin_onboarding:finish_step', { step_name: 'network_type' });
          return `/networks/${slug}/welcome/onboarding/goals`;
        case 'goals':
          setGoals(props);
          trackEvent('admin_onboarding:finish_step', { step_name: 'goals' });
          return `/networks/${slug}/welcome/onboarding/${isExtensionInstalled ? 'share-contacts' : 'linkedin-sync'}`;
        case 'linkedin-sync':
          trackEvent('admin_onboarding:finish_step', { step_name: 'linkedin_sync' });
          return `/networks/${slug}/welcome/onboarding/team`;
        case 'share-contacts':
          trackEvent('admin_onboarding:finish_step', { step_name: 'share_contacts' });
          return `/networks/${slug}/welcome/onboarding/team`;
        case 'team':
          trackEvent('admin_onboarding:finish_step', { step_name: 'invite_team' });
          trackEvent('admin_onboarding:complete');

          return `/networks/${slug}/home`;
        default:
          trackEvent('admin_onboarding:complete');

          return `/networks/${slug}/home`;
      }
    },
    [network, companyNetwork?.slug, isExtensionInstalled],
  );

  const handleNextStep = useCallback(
    async (props) => {
      const nextStep = await getNextStep(params.step, props);
      history.push(nextStep);
    },
    [getNextStep, history, params],
  );

  const updateUserProfile = useCallback(
    async ({ avatarUrl, firstName, lastName, password }) => {
      const options = {
        avatarUrl,
        firstName,
        lastName,
        password,
        currentPassword: user.password,
        passwordConfirmation: password,
        termsPolicyVersion: legalDocumentsVersion.termsPolicyVersion,
        privacyPolicyVersion: legalDocumentsVersion.privacyPolicyVersion,
        onboardingCompleted: !network?.connect?.hasAccess,
      };

      const response = await updateUser(options).catch((error) => {
        trackException(error);

        throw error;
      });

      // if it's company onboarding, claim the organization
      if (params.employerSlug) {
        const { error: claimError, data: claimResponse } = await claimOrganization({
          collectionId: companyNetwork?.id,
          organizationId: company?.id,
        });
        if (claimError) {
          toast(
            claimError?.data?.code === 'company_claim.domain_not_valid'
              ? claimError?.data?.error
              : strings.genericError,
            {
              icon: 'error',
              id: 'claim-organization',
            },
          );
          return response;
        }

        const { data, error } = await getUser();
        if (error) {
          toast(strings.genericError, {
            icon: 'error',
            id: 'get-user',
          });
          return response;
        }
        dispatch(setUser(data));
        dispatch(setNetworks(data));
        const networkData = data.managingCollections.find(
          (collection) => collection.slug === claimResponse?.companyCollection?.slug,
        );
        dispatch(setActiveNetwork(networkData.id));
        history.push(`/networks/${networkData.slug}/welcome/onboarding/goals`);

        return response;
      }

      if (!network?.connect?.hasAccess) {
        dispatch(
          setUser({
            avatarUrl,
            firstName,
            lastName,
            onboardingCompleted: true,
          }),
        );
        dispatch(
          updateNetwork({
            id: network.id,
            kind: networkKind,
          }),
        );
        history.push(`/networks/${network?.slug}/home`);
      }

      handleNextStep();

      return response;
    },
    [
      user.password,
      network?.connect?.hasAccess,
      network?.slug,
      network?.id,
      updateUser,
      params.employerSlug,
      handleNextStep,
      companyNetwork?.id,
      dispatch,
      networkKind,
      history,
      claimOrganization,
      company?.id,
      getUser,
    ],
  );

  const updateNetworkKind = async (kind) => {
    setNetworkKind(kind);
    const response = await updateKind({ networkId: network?.id, kind });

    if (response.error) {
      toast(strings.genericError, {
        icon: 'error',
        id: 'update-network-kind',
      });
      return;
    }

    dispatch(
      updateNetwork({
        id: network?.id,
        kind: networkKind,
        kindSetupByCurrentUser: true,
      }),
    );

    handleNextStep(kind);
  };

  const updateGoals = async (goalsToUpdate) => {
    setGoals(goalsToUpdate);

    const response = await updateUserGoals({ networkId: network?.id, goals: Array.from(goalsToUpdate) });

    if (response.error) {
      toast(strings.genericError, {
        icon: 'error',
        id: 'update-user-goals',
      });
      return;
    }

    handleNextStep(goalsToUpdate);
  };

  const updateTeam = async (props) => {
    setUsers((prev) => {
      const map = new Map();
      props.usersToAdd.forEach((item) => {
        map.set(item.id, item);
      });
      return prev.map((item) => {
        if (map.has(item.id)) {
          return map.get(item.id);
        }
        return item;
      });
    });

    if (!props.skip && props.usersToAdd.length > 0) {
      const results = await addAdmin(props.usersToAdd);
      const hasFailure = results.some((r) => r.value.error);
      if (hasFailure) {
        toast(strings.genericError, {
          icon: 'error',
          id: 'update-team',
        });

        return;
      }
    }
    const { avatarUrl, firstName, lastName } = user;

    if (!user.onboardingCompleted) {
      await updateUser({
        onboardingCompleted: true,
      });
    }

    dispatch(
      setUser({
        avatarUrl,
        firstName,
        lastName,
        kind: networkKind,
        goals: Array.from(goals),
        onboardingCompleted: true,
      }),
    );

    dispatch(
      updateNetwork({
        id: network?.id,
        kind: networkKind,
        userGoals: Array.from(goals),
        userGoalsAnswered: true,
      }),
    );

    handleNextStep(props);
  };

  return {
    isLoadingCompanyNetwork: isLoadingCompanyNetwork || isFetchingCompanyNetwork,
    activeNetwork: network,
    networks,
    path,
    isLoadingUpdateUser: isLoadingUpdateUser || isClaimingOrganization || isLoadingGetUser,
    updateUserProfile,
    handleNextStep,
    users,
    networkKind,
    goals,
    setUsers,
    setNetworkKind,
    setGoals,
    updateUserGoals,
    isLoadingUpdateUserGoals,
    updateNetworkKind,
    isLoadingUpdateNetworkKind,
    isLoadingAddAdmin: isLoadingAddAdmin || isLoadingUpdateUser,
    user,
    params,
    updateTeam,
    updateGoals,
    showNetworkKindStep: network?.kindSetupByCurrentUser || network?.kindSetupAt === null || !network?.kind,
    skipSignUp:
      user?.onboardingCompleted && (user.hasPassword || user.oauthProviders.length > 0) && !params.employerSlug,
    profileMode: user?.hasPassword || user?.oauthProviders.length > 0 ? 'password' : 'email',
    isExtensionInstalled,
    companyNetwork,
    company,
  };
};
